import { faAngleRight, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { zodResolver } from "@hookform/resolvers/zod/dist/zod"
import { exhaustive } from "exhaustive"
import { isPlainObject, sortBy } from "lodash"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { MeResponseConsumerData } from "../../Auth/Auth.types"
import { useAuth } from "../../Auth/AuthContext"
import { useClient } from "../../Client/ClientAndUserProvider"
import { Loader } from "../../Loader/Loader"
import { useNavigator } from "../../Navigator/useNavigator"
import { API, ServerError } from "../../network/API"
import { FieldModuleBox } from "../../Orders/Components/FieldModuleBox/FieldModuleBox"
import { FieldModuleBoxWrapper } from "../../Orders/Components/FieldModuleBox/FieldModuleBoxWrapper"
import { DenyButton, FinalizeButton } from "../../Orders/Components/Form/Buttons/Buttons"
import { MultiStateSwitch } from "../../Orders/Components/Form/MultiStateSwitch/MultiStateSwitch"
import { ModulChooserButton } from "../../Orders/Components/ModulChooserButton/ModulChooserButton"
import { ModulePopup } from "../../Orders/Components/ModulePopup/ModulePopup"
import orderConfirmStyle from "../../Orders/Components/OrderConfirmCustomerInformation/OrderConfirmCustomerInformation.module.css"
import { SbtH1 } from "../../Orders/Components/Text/SbtH1/SbtH1"
import { SbtH4 } from "../../Orders/Components/Text/SbtH4/SbtH4"
import { SbtRHFError } from "../../Orders/Components/Text/SbtInvalid/SbtRHFError"
import { notNull } from "../../Shared/notNull"
import { GetConsumerTag, GetConsumerTags } from "../CustomerPortalConsumerTags/CustomerPortalConsumerTags"
import style from "./AddEditConsumerTagModal.module.css"

export const NewGenericTagFormSchema = z.object({
	text: z.string().min(1).max(35, "För långt tagg-namn"),
})

type NewGenericTagFormType = z.input<typeof NewGenericTagFormSchema>

type CreateGenericConsumerTag = {
	type: "Generic"
	text: string
}

type CreateConsumerTag = CreateGenericConsumerTag

type PostCreateConsumerTag = {
	tag: CreateConsumerTag
}

type UpdateGenericConsumerTag = {
	type: "Generic"
	tagId: string
	text: string
}

type UpdateConsumerTag = UpdateGenericConsumerTag

type PostUpdateConsumerTag = {
	tag: UpdateConsumerTag
}

type PostSetConsumerTagConnections = {
	tagIds: string[]
}

export type ConsumerTagModalModeAddGlobal = {
	type: "Add"
}

export type ConsumerTagModalModeEdit = {
	type: "Edit"
	tag: GetConsumerTag
}

export type ConsumerTagModalModeConsumerView = {
	type: "ConsumerView"
	consumerId: string
	defaultView: TagModalView.AddTag | TagModalView.SelectExistingTags
	tag: GetConsumerTag | null
}

export type ConsumerTagModalMode =
	| ConsumerTagModalModeAddGlobal
	| ConsumerTagModalModeEdit
	| ConsumerTagModalModeConsumerView

export type AddEditConsumerTagModalProps = {
	mode: ConsumerTagModalMode
	onClose: () => void
	onDone: () => void
}

export enum TagModalView {
	EditTag = "EditTag",
	AddTag = "AddTag",
	SelectExistingTags = "SelectExistingTags",
}

enum SubmitFailType {
	Generic = "Generic",
	DuplicatedName = "DuplicatedName",
	TagNotFound = "TagNotFound",
}

export const AddEditConsumerTagModal: FC<AddEditConsumerTagModalProps> = (props: AddEditConsumerTagModalProps) => {
	const client = useClient()
	const auth = useAuth()
	const navigator = useNavigator()

	const form = useRef<HTMLFormElement | null>(null)
	const [submitting, setSubmitting] = useState(false)
	const [submitFailed, setSubmitFailed] = useState<SubmitFailType | null>(null)

	const [selectedView, setSelectedView] = useState<TagModalView>(() => {
		return exhaustive(props.mode, "type", {
			Add: () => {
				return TagModalView.AddTag
			},
			ConsumerView: (it) => {
				return it.defaultView
			},
			Edit: () => {
				return TagModalView.EditTag
			},
		})
	})

	const [currentTags, setCurrentTags] = useState<GetConsumerTag[] | null>(null)

	const [currentConsumerSelectedTagIds, setCurrentConsumerSelectedTagIds] = useState<string[]>([])

	const fetchAvailableTags = useCallback(() => {
		const mode = props.mode
		setCurrentTags(null)
		API.getWithRetries<GetConsumerTags>(`/customer-portal/consumer-tags-v1/${client.identifier}`, true)
			.then((res) => {
				setCurrentTags(res.tags)

				if (mode.type === "ConsumerView") {
					const consumerSelectedTagIds = res.tags
						.map((x) => {
							if (x.consumerRefs.includes(mode.consumerId)) {
								return x.id
							}

							return null
						})
						.filter(notNull)
					setCurrentConsumerSelectedTagIds(consumerSelectedTagIds)
				}
			})
			.catch(() => {
				setCurrentTags([])
			})
	}, [client.identifier, props.mode])

	useEffect(() => {
		exhaustive(selectedView, {
			EditTag: () => {},
			AddTag: () => {},
			SelectExistingTags: () => {
				fetchAvailableTags()
			},
		})
	}, [fetchAvailableTags, selectedView])

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<NewGenericTagFormType>({
		mode: "onChange",
		resolver: async (data, context, options) => {
			return zodResolver(NewGenericTagFormSchema)(data, context, options)
		},
		defaultValues: {
			text: exhaustive(props.mode, "type", {
				Add: () => {
					return ""
				},
				ConsumerView: () => {
					return ""
				},
				Edit: (it) => {
					return it.tag?.text || ""
				},
			}),
		},
	})

	function onSubmit(formData: NewGenericTagFormType) {
		setSubmitting(true)
		setSubmitFailed(null)

		exhaustive(props.mode, "type", {
			Add: () => {
				createGenericTag(formData.text)
			},
			ConsumerView: (it) => {
				if (selectedView === TagModalView.AddTag) {
					createAndConnectGenericTag(formData.text, it.consumerId)
				}
			},
			Edit: (it) => {
				updateGenericTag(it.tag.id, formData.text)
			},
		})
	}

	function handleRequestError(err: any) {
		setSubmitting(false)
		if (err instanceof ServerError) {
			if (err?.data && isPlainObject(err?.data) && "message" in err?.data) {
				const message = err.data.message

				if (message === "duplicated_name") {
					setSubmitFailed(SubmitFailType.DuplicatedName)
				} else if (message === "tag_not_found") {
					setSubmitFailed(SubmitFailType.TagNotFound)
				} else {
					setSubmitFailed(SubmitFailType.Generic)
				}
				return
			}
		}
		setSubmitFailed(SubmitFailType.Generic)
	}

	function createGenericTag(text: string) {
		const req: PostCreateConsumerTag = {
			tag: {
				type: "Generic",
				text: text,
			},
		}

		API.postWithRetries<GetConsumerTags, PostCreateConsumerTag>(
			`/customer-portal/consumer-tags-v1/${client.identifier}/create`,
			req,
			undefined,
			10,
		).then(
			() => {
				props.onDone()
			},
			(err) => {
				handleRequestError(err)
			},
		)
	}

	function createAndConnectGenericTag(text: string, consumerId: string) {
		const req: PostCreateConsumerTag = {
			tag: {
				type: "Generic",
				text: text,
			},
		}

		API.postWithRetries<GetConsumerTags, PostCreateConsumerTag>(
			`/customer-portal/consumer-tags-v1/${client.identifier}/create-and-connect/${consumerId}`,
			req,
			undefined,
			10,
		).then(
			() => {
				props.onDone()
			},
			(err) => {
				handleRequestError(err)
			},
		)
	}

	function setConnectedTagsForConsumer(consumerId: string, tagIds: string[]) {
		setSubmitting(true)
		setSubmitFailed(null)

		let req: PostSetConsumerTagConnections = {
			tagIds: tagIds,
		}

		API.postWithRetries<void, PostSetConsumerTagConnections>(
			`/customer-portal/consumer-tags-v1/${client.identifier}/${consumerId}/set-connections`,
			req,
			undefined,
			10,
		).then(
			() => {
				props.onDone()
			},
			(err) => {
				handleRequestError(err)
			},
		)
	}

	function updateGenericTag(tagId: string, text: string) {
		const req: PostUpdateConsumerTag = {
			tag: {
				type: "Generic",
				tagId: tagId,
				text: text,
			},
		}
		API.postWithRetries<void, PostUpdateConsumerTag>(
			`/customer-portal/consumer-tags-v1/${client.identifier}/${tagId}/update`,
			req,
			undefined,
			10,
		).then(
			() => {
				props.onDone()
			},
			(err) => {
				handleRequestError(err)
			},
		)
	}

	function deleteTag(tagId: string) {
		setSubmitting(true)
		setSubmitFailed(null)
		API.deleteWithRetries<void>(
			`/customer-portal/consumer-tags-v1/${client.identifier}/${tagId}/delete`,
			undefined,
			undefined,
			10,
		).then(
			() => {
				props.onDone()
			},
			(err) => {
				handleRequestError(err)
			},
		)
	}

	function existingTagsSelection() {
		const mode = props.mode

		if (mode.type !== "ConsumerView" || selectedView !== TagModalView.SelectExistingTags) {
			return null
		}

		if (!currentTags) {
			return (
				<div className={style.existingTagsSection}>
					<Loader />
				</div>
			)
		}

		if (currentTags.length === 0) {
			return (
				<div className={style.existingTagsSection}>
					<div className={style.existingTagsHeader}>
						Det finns inga taggar att välja för tillfället.{" "}
						<span
							style={{ textDecoration: "underline", cursor: "pointer" }}
							onClick={() => {
								setSelectedView(TagModalView.AddTag)
							}}>
							Lägg till en ny
						</span>
					</div>
				</div>
			)
		}

		return (
			<div className={style.existingTagsSection}>
				<div className={style.tagsWrapper}>
					{currentTags.map((tag) => {
						return exhaustive(tag, "type", {
							Generic: (it) => {
								return (
									<div
										key={it.id}
										className={style.tag}
										onClick={() => {
											if (currentConsumerSelectedTagIds.includes(it.id)) {
												setCurrentConsumerSelectedTagIds([
													...currentConsumerSelectedTagIds.filter((x) => x !== it.id),
												])
											} else {
												setCurrentConsumerSelectedTagIds([...currentConsumerSelectedTagIds, it.id])
											}
										}}>
										{it.text}
										<input
											type={"checkbox"}
											checked={currentConsumerSelectedTagIds.includes(it.id)}
											onChange={() => {}}
										/>
									</div>
								)
							},
						})
					})}
				</div>
			</div>
		)
	}

	function content() {
		return exhaustive(props.mode, "type", {
			Add: () => {
				return addEditTagForm()
			},
			ConsumerView: () => {
				return (
					<>
						<MultiStateSwitch
							selectedValue={selectedView}
							onSelect={(option, initial) => {
								if (initial || option.key === selectedView) {
									return
								}

								setSelectedView(option.key)
							}}
							options={[
								{ key: TagModalView.SelectExistingTags, value: "Existerande taggar" },
								{ key: TagModalView.AddTag, value: "Ny tagg" },
							]}
						/>
						{selectedView === TagModalView.AddTag ? addEditTagForm() : null}
						{selectedView === TagModalView.SelectExistingTags ? existingTagsSelection() : null}
					</>
				)
			},
			Edit: () => {
				return addEditTagForm()
			},
		})
	}

	function addEditTagForm() {
		const mode = props.mode
		return (
			<>
				<form style={{ marginTop: "20px" }} onSubmit={handleSubmit(onSubmit)} ref={form} id="addEditForm">
					<div className={orderConfirmStyle.fields}>
						<label>
							<SbtH4>Tagg*</SbtH4>
							<input disabled={submitting} className={orderConfirmStyle.input} {...register("text")} />
							<SbtRHFError error={errors.text} />
						</label>
					</div>
				</form>
				{mode.type === "Edit" ? consumersBelongingToEditedTag(mode.tag) : null}
			</>
		)
	}

	function consumersBelongingToEditedTag(tag: GetConsumerTag) {
		const consumers = auth.Me?.consumers || []

		if (consumers.length === 0) {
			return null
		}

		let belongingConsumers: MeResponseConsumerData[] = tag.consumerRefs
			.map((consumerRef) => {
				const consumer = consumers.find((x) => x.id === consumerRef)
				return consumer || null
			})
			.filter(notNull)

		return (
			<div>
				<div className={style.existingConsumersHeader}>Tillhörande kunder</div>
				<FieldModuleBoxWrapper className={style.connectedConsumerFieldWrapper}>
					{sortBy(belongingConsumers, (x) => x.name.normalize("NFC").toLocaleLowerCase()).map((consumer) => {
						return (
							<FieldModuleBox className={style.connectedConsumerField} key={consumer.id}>
								<strong>{consumer.name}</strong>
								<ModulChooserButton
									className={style.connectedConsumerFieldButton}
									selected={false}
									onClick={() => {
										navigator.open(`my-pages/consumers?tab=overview&consumerId=${consumer.id}`)
									}}>
									<FontAwesomeIcon className={style.connectedConsumerFieldIcon} icon={faAngleRight} />
								</ModulChooserButton>
							</FieldModuleBox>
						)
					})}
				</FieldModuleBoxWrapper>
			</div>
		)
	}

	const mode = props.mode

	return (
		<ModulePopup
			disableClose={submitting}
			className={style.modal}
			onClose={() => {
				props.onClose()
			}}>
			<div className={style.wrapper}>
				{mode.type === "Add" ? <SbtH1>Lägg till tagg</SbtH1> : null}
				{mode.type === "Edit" ? <SbtH1>Redigera tagg</SbtH1> : null}
				{mode.type === "ConsumerView" ? <SbtH1>Taggar</SbtH1> : null}
				<div className={style.content}>{content()}</div>
				<div className={style.buttons}>
					<FinalizeButton
						disabled={submitting}
						onClick={() => {
							if (selectedView === TagModalView.AddTag || selectedView === TagModalView.EditTag) {
								form.current?.requestSubmit()
							} else if (mode.type === "ConsumerView" && selectedView === TagModalView.SelectExistingTags) {
								setConnectedTagsForConsumer(mode.consumerId, currentConsumerSelectedTagIds)
							}
						}}>
						{mode.type === "Add" ? "Lägg till" : null}
						{mode.type === "Edit" || mode.type === "ConsumerView" ? "Spara" : null}
						{submitting ? (
							<FontAwesomeIcon style={{ color: "gray", marginLeft: "15px" }} spin={true} icon={faSpinner} />
						) : null}
					</FinalizeButton>
					{mode.type === "Edit" ? (
						<DenyButton
							disabled={submitting}
							onClick={() => {
								deleteTag(mode.tag.id)
							}}>
							Ta bort
							{submitting ? (
								<FontAwesomeIcon
									style={{ color: "gray", marginLeft: "15px" }}
									spin={true}
									icon={faSpinner}
								/>
							) : null}
						</DenyButton>
					) : null}
				</div>
				{submitFailed ? (
					<div style={{ color: "var(--invalid-color)", margin: "15px 0 10px 0" }}>
						{exhaustive(submitFailed, {
							Generic: () => "Något gick fel, vänligen försök igen",
							DuplicatedName: () => "Namnet är en dublett av en annan tagg, vänligen försök igen",
							TagNotFound: () => "Taggen kunde inte hittas, vänligen ladda om sidan och försök igen",
						})}
					</div>
				) : null}
			</div>
		</ModulePopup>
	)
}
